import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toast } from 'react-hot-toast';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  features: {
    storage: number;
    social_accounts: number;
    ai_credits: number;
    analytics: boolean;
    advanced_tools: boolean;
    [key: string]: any;
  };
}

interface UserSubscription {
  plan: string;
  status: string;
  periodEnd: Date | null;
  stripeCustomerId: string | null;
}

export function useSubscription() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription>({
    plan: 'free',
    status: 'active',
    periodEnd: null,
    stripeCustomerId: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Load subscription plans
      const { data: plansData, error: plansError } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price', { ascending: true });

      if (plansError) throw plansError;
      setPlans(plansData || []);

      // Load user's current subscription
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('subscription_plan, subscription_status, subscription_period_end, stripe_customer_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profile) {
        setCurrentSubscription({
          plan: profile.subscription_plan || 'free',
          status: profile.subscription_status || 'active',
          periodEnd: profile.subscription_period_end ? new Date(profile.subscription_period_end) : null,
          stripeCustomerId: profile.stripe_customer_id || null
        });
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const createCheckoutSession = async (planId: string): Promise<{ url?: string; error?: string }> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        return { error: 'Not authenticated. Please log in again.' };
      }

      // Create checkout session
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId,
          userId: user.id,
          returnUrl: `${window.location.origin}/subscription`,
        }),
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          return { error: errorData.error || 'Failed to create checkout session' };
        } catch (e) {
          return { error: 'Failed to create checkout session. Server returned an invalid response.' };
        }
      }

      const { url } = await response.json();
      return { url };
    } catch (err) {
      console.error('Error creating checkout session:', err);
      return { error: err instanceof Error ? err.message : 'Failed to create checkout session' };
    } finally {
      setLoading(false);
    }
  };

  const checkFeatureAccess = (feature: string): boolean => {
    // Default feature access for free plan
    const defaultAccess: Record<string, boolean> = {
      'basic_analytics': true,
      'advanced_analytics': false,
      'ai_tools': false,
      'unlimited_storage': false,
      'unlimited_social': false,
      'priority_support': false,
      'custom_branding': false,
      'team_collaboration': false,
      'advanced_automation': false,
      'content_calendar': true,
      'basic_scheduling': true,
      'advanced_scheduling': false
    };

    // If plans aren't loaded yet, use default access
    if (plans.length === 0) {
      return defaultAccess[feature] || false;
    }

    // Find current plan
    const currentPlan = plans.find(p => p.name === currentSubscription.plan);
    if (!currentPlan) return defaultAccess[feature] || false;

    // Check if feature exists in plan features
    if (feature in currentPlan.features) {
      return !!currentPlan.features[feature];
    }

    // Special handling for specific features
    switch (feature) {
      case 'advanced_analytics':
        return currentPlan.features.analytics === true;
      case 'ai_tools':
        return currentPlan.name !== 'free';
      case 'unlimited_storage':
        return currentPlan.features.storage >= 100;
      case 'unlimited_social':
        return currentPlan.features.social_accounts >= 5;
      case 'advanced_automation':
      case 'team_collaboration':
      case 'custom_branding':
      case 'priority_support':
        return currentPlan.name === 'supercharged';
      case 'advanced_scheduling':
        return currentPlan.name !== 'free';
      default:
        return defaultAccess[feature] || false;
    }
  };

  return {
    plans,
    currentSubscription,
    loading,
    error,
    createCheckoutSession,
    checkFeatureAccess,
    refresh: loadSubscriptionData
  };
}